import { AccountTree } from "@mui/icons-material"
import { Box, Button, Paper } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../auth"
import CustomToolbar from "../components/CustomToolbar"
import DialogConfirm from "../components/DialogConfirm"
import DialogForm from "../components/DialogForm"
import IconButtonDelete from "../components/IconButtonDelete"
import LangLink from "../components/LangLink"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import AssemblaggioForm from "../components/forms/AssemblaggioForm"
import {
  useAssemblaggiList,
  useAssemblaggioCreate,
  useAssemblaggioDelete,
  useAssemblaggioUpdate,
} from "../hooks/assemblaggi"
import { useQsFilters } from "../hooks/filters"
import { useIsMobile } from "../hooks/mobile"
import useUserCanWriteAndDelete from "../hooks/permissions"
import { useSnackbarMessage } from "../hooks/snackbar"
import { useIsTablet } from "../hooks/tablet"
import { Assemblaggio } from "../types"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "nome",
})

export default function Assemblaggi() {
  const pageSize = usePageSize()
  const { filters, setFilters, setFiltersDebounced, uiFilters } = useQsFilters(initFilters)
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const canWrite = useUserCanWriteAndDelete()
  const showMessage = useSnackbarMessage()
  const { user } = useAuth()
  const isMobile = useIsMobile() || user?.display === "mobile"
  const isTablet = (useIsTablet() || user?.display === "tablet") && !isMobile
  const lang = i18n.language

  useEffect(() => {
    if (user?.ruolo === "consultazione") {
      navigate(`/${lang}/fondi`)
    }
  }, [user, lang, navigate])

  const [createModalState, createModalActions] = useModalTrigger()
  const [editModalState, editModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      {
        field: "nome",
        headerName: t("assemblaggio.nome"),
        flex: 1,
        renderCell: (params) => (
          <LangLink style={{ color: "black" }} to={`/assemblaggi/${params.row.id}`}>
            {params.row.nome}
          </LangLink>
        ),
      },
      {
        field: "numero_supporti",
        headerName: t("assemblaggio.numero_supporti"),
        flex: isMobile ? 0.5 : 1,
      },
    ]

    if (!isMobile && canWrite) {
      cols.push({
        field: "id",
        headerName: t("general.azioni"),
        flex: isTablet ? 1 : 0.5,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) => (
          <>
            <IconButtonDelete onClick={() => deleteModalActions.open(params.row)} disabled={!params.row.can_delete} />
          </>
        ),
      })
    }

    return cols
  }, [t, canWrite, isMobile, isTablet])

  const { data, isLoading } = useAssemblaggiList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
  })
  const { mutateAsync: create } = useAssemblaggioCreate()
  const { mutateAsync: update } = useAssemblaggioUpdate()
  const { mutateAsync: remove } = useAssemblaggioDelete()

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <PageTitle
            title={t("assemblaggio.titolo_lista")}
            breadcrumbs={[
              {
                label: t("assemblaggio.assemblaggi"),
                iconType: AccountTree,
              },
            ]}
          />
          {canWrite && !isMobile && (
            <Button variant="contained" color="primary" onClick={() => createModalActions.open()}>
              {t("assemblaggio.cta_aggiungi")}
            </Button>
          )}
        </Box>
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() => setFilters({ search: "", page: 1 })}
              filtersCount={0}
              ref={toolbarRef}
            />
            <div style={{ flexGrow: 1, overflow: "scroll", height: tableHeight ?? undefined }} ref={ref}>
              {height && (
                <DataGrid
                  disableColumnMenu
                  disableColumnResize
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{ padding: 2, "&.MuiDataGrid-root": { border: "none" } }}
                  localeText={{ noRowsLabel: t("general.nessun_dato_disponibile") }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) => setFilters({ ...filters, page })}
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      setFilters({ ordering: `${sortSign}${model[0].field}`, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <DialogForm<Assemblaggio>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("assemblaggio.cta_aggiungi")}
        FormComponent={AssemblaggioForm}
        hasDoubleLanguage
        save={(values) =>
          create(values).then((response) => {
            createModalActions.close()
            showMessage(t("assemblaggio.esito_crea_ok"))
            navigate(`/${i18n.language}/assemblaggi/${response.id}`)
          })
        }
      />
      <DialogForm<Assemblaggio>
        open={editModalState.isOpen}
        handleClose={editModalActions.close}
        title={t("assemblaggio.cta_modifica")}
        FormComponent={AssemblaggioForm}
        item={editModalState.value}
        hasCancel
        hasDoubleLanguage
        save={(values) =>
          update(values).then(() => {
            editModalActions.close()
            showMessage(t("assemblaggio.esito_modifica_ok"))
          })
        }
      />
      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("assemblaggio.cta_elimina")}
        subtitle={
          <span>
            {t("assemblaggio.cta_subtitle_elimina")} <b>"{deleteModalState.value?.nome}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() =>
          remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("assemblaggio.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }
      />
    </Layout>
  )
}
